// externals
import { isEqual } from 'lodash';
import type { FC } from 'react';
import { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Custom internal components
import RollToPaymentGrid from './RollToPaymentGrid';
import RollToPaymentHeader from './RollToPaymentHeader';

// interstate components
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';

// utils
import { formatDollarsAndCents, formatNumberAndCents } from '@makemydeal/dr-common-utils';
import { buildRateRow } from './buildRateRow';
import { generateCashDownLabel } from '../../terms/common/utils';

// selectors and actions
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as rollToActionCreators from '@makemydeal/dr-dash-store/actions/rollToActionCreators';
import * as rollToSelectors from '@makemydeal/dr-dash-store/selectors/rollToSelectors';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';

// types
import type { RollToOption } from '@makemydeal/dr-platform-types';
import { ExtendedOfferTypeEnum } from '@makemydeal/dr-platform-types';
import { ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';
import { debounce } from 'lodash';
import type { Row } from '../../types/RollToPaymentOptionRow';
import { ADJUSTABLE_FIELDS } from './constants/adjustableFields';
import type { RollToActions } from '@makemydeal/dr-dash-types';

const headers = ['', 'Current', 'Roll-To', 'Adjust'];
const defaultDebounceDuration = 1000;
// empty cell display
const emptyCell = '--';

type RollToPaymentOptionsProps = {
    activeTab: ROLL_TO_OPTIONS_LABELS;
};

const RollToPaymentOptions: FC<RollToPaymentOptionsProps> = ({ activeTab }) => {
    const dispatch = useDispatch();
    const values = useRef<Record<number, number | undefined> | null>(null);
    const rollToOptionsItems = useSelector(rollToSelectors.getRollToOptionsItems);
    const hasRollToOptions = useSelector(rollToSelectors.hasRollToOptionsItems);
    const isTradeInCompleted = useSelector(tradeInSelectors.isTradeInCompleted);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const rateType = useSelector(offerReduxSelectors.getRateType);

    const deductDueAtSigning = useSelector(dealerSelectors.getIsDueAtSigninAmountDeduct);
    const isOnePayLease = useSelector(offerReduxSelectors.isOnePayLease);
    const isTargetOutOfPocket = useSelector(offerReduxSelectors.getIsTargetOutOfPocket);
    const isEnhancedDealDetailsEnabled = useSelector(featureToggleSelectors.getEnhancedDealDetails);
    const isTargetOutOfPocketLeaseEnabled = useSelector(featureToggleSelectors.isTargetOutOfPocketLeaseEnabled);
    const isDueAtSigningEnhancementsEnabled = useSelector(featureToggleSelectors.enableDueAtSigningEnhancements);

    const isLease = offerType === ExtendedOfferTypeEnum.LEASE;
    const isProfitTab = activeTab === ROLL_TO_OPTIONS_LABELS.PROFIT;
    const isBalanceDueTab = activeTab === ROLL_TO_OPTIONS_LABELS.BALANCE_DUE;
    const isCashDueTab = activeTab === ROLL_TO_OPTIONS_LABELS.CASH_DUE;
    const isPaymentTab = activeTab === ROLL_TO_OPTIONS_LABELS.PAYMENT;
    const isBalloon = offerType === ExtendedOfferTypeEnum.BALLOON;
    const { rollTo, current, adjust } = rollToOptionsItems;

    // term
    const currentTerm = current?.term || 0;
    const adjustTerm = adjust?.term || 0;
    const rollToTerm = rollTo?.term;

    // rate
    const currentRate = current?.rate || 0;
    const adjustRate = adjust?.rate || 0;
    const rollToRate = rollTo?.rate;

    // down payment
    const currentDownPayment = formatDollarsAndCents(current?.downPayment, emptyCell);
    const adjustDownPayment = adjust?.downPayment || 0;
    const rollToDownPayment = formatDollarsAndCents(rollTo?.downPayment, emptyCell);

    // selling price
    const currentSellingPrice = formatDollarsAndCents(current?.sellingPrice, emptyCell);
    const adjustSellingPrice = adjust?.sellingPrice || 0;
    const rollToSellingPrice = formatDollarsAndCents(rollTo?.sellingPrice, emptyCell);

    // balloon amount
    const currentBalloonAmount = formatDollarsAndCents(current?.balloonAmount, emptyCell);
    const adjustBalloonAmount = adjust?.balloonAmount || 0;
    const rollToBalloonAmount = formatDollarsAndCents(rollTo?.balloonAmount, emptyCell);

    const getTrades = (obj: RollToActions['current'] | RollToActions['rollTo'] | RollToActions['adjust']) => obj?.tradeIns || [];

    const currentTrades = getTrades(current);
    const rollToTrades = getTrades(rollTo);
    const adjustTrades = getTrades(adjust);

    // taxes & fees
    const currentTaxesAndFeesAsStr = formatDollarsAndCents(current?.taxesAndFees?.toString() || '');
    const adjustTaxesAndFeesAsStr = formatDollarsAndCents(adjust?.taxesAndFees?.toString());

    // add ons
    const addOnsAsStr = formatDollarsAndCents(current?.addOns?.toString() || '');
    const adjustAddOnsAsStr = formatDollarsAndCents(adjust?.addOns?.toString() || '');

    // action values
    const currentActionValue = formatDollarsAndCents(current?.actionValue?.toString() || '');
    const adjustActionValue = formatDollarsAndCents(adjust?.actionValue?.toString() || '');
    const rollToActionValue = formatDollarsAndCents(rollTo?.actionValue?.toString() || '');

    const cashDownLabel = generateCashDownLabel(
        offerType,
        isOnePayLease ? false : deductDueAtSigning,
        isTargetOutOfPocket,
        isEnhancedDealDetailsEnabled,
        isTargetOutOfPocketLeaseEnabled,
        isDueAtSigningEnhancementsEnabled
    );

    const rollToRows = useMemo(() => {
        let rows: Row[] = [
            ...(!isBalloon
                ? [
                      {
                          label: ADJUSTABLE_FIELDS.TERM,
                          current: currentTerm,
                          rollTo: formatNumberAndCents(rollToTerm),
                          rollToRaw: rollToTerm,
                          key: 'term',
                          adjust: adjustTerm,
                          isAdjustableField: true
                      }
                  ]
                : []),
            buildRateRow({ rateType, currentRate, rollToRate, adjustRate }),
            {
                label: cashDownLabel,
                current: currentDownPayment,
                rollTo: rollToDownPayment,
                rollToRaw: rollTo?.downPayment,
                adjust: adjustDownPayment,
                key: 'downPayment',
                isAdjustableField: true,
                showDealPulseTooltip: true,
                inputPrefix: '$'
            },
            {
                label: ADJUSTABLE_FIELDS.SELLING_PRICE,
                current: currentSellingPrice,
                rollTo: rollToSellingPrice,
                rollToRaw: rollTo?.sellingPrice,
                adjust: adjustSellingPrice,
                key: 'sellingPrice',
                isAdjustableField: true,
                inputPrefix: '$'
            },
            ...(isBalloon
                ? [
                      {
                          label: ADJUSTABLE_FIELDS.BALLOON_AMOUNT,
                          current: currentBalloonAmount,
                          rollTo: rollToBalloonAmount,
                          rollToRaw: rollTo?.balloonAmount,
                          adjust: adjustBalloonAmount,
                          key: 'balloonAmount',
                          isAdjustableField: true,
                          inputPrefix: '$'
                      }
                  ]
                : []),
            ...currentTrades.map((currentTrade, i) => {
                const rollToTrade = rollToTrades[i];
                const adjustTrade = adjustTrades[i];
                return {
                    label: `Trade ${i + 1} Allowance`,
                    current: formatDollarsAndCents(currentTrade?.value, emptyCell),
                    rollTo: formatDollarsAndCents(rollToTrade?.value, emptyCell),
                    rollToRaw: rollToTrade?.value,
                    adjust: adjustTrade?.value || 0,
                    key: `trade_${i + 1}`,
                    callerItemId: currentTrade?.callerItemId,
                    isAdjustableField: true,
                    inputPrefix: '$',
                    isTradeInRow: true
                };
            }),
            {
                label: 'Total Taxes and Fees',
                current: currentTaxesAndFeesAsStr,
                rollTo: emptyCell,
                adjust: adjustTaxesAndFeesAsStr,
                key: 'taxesAndFees'
            },
            {
                label: 'Total Add Ons',
                current: addOnsAsStr,
                rollTo: emptyCell,
                adjust: adjustAddOnsAsStr,
                dividerBelow: true,
                key: 'addOns'
            },
            {
                // @note the label must be dynamic, since depends on the current tab
                // We have different labels for profit tab and for others its same as activeTab name
                label: activeTab === ROLL_TO_OPTIONS_LABELS.PROFIT ? ROLL_TO_OPTIONS_LABELS.FRONT_PROFIT : activeTab,
                current: currentActionValue,
                rollTo: rollToActionValue,
                adjust: adjustActionValue,
                key: 'actionValue',
                bold: true
            }
        ];

        // Filter out trade rows if trade-in not completed AND no trade values exist
        const hasTradeValues = currentTrades.length > 0 && currentTrades.some((trade) => (trade?.value ?? 0) > 0);
        if (!isTradeInCompleted && !hasTradeValues) {
            rows = rows.filter((rollToRow) => !rollToRow.isTradeInRow);
        }
        if ((isLease && isPaymentTab) || isProfitTab || isBalanceDueTab || isCashDueTab) {
            rows = rows.filter((rollToRow) => rollToRow.key !== 'term');
        }
        if ((!isLease && isBalanceDueTab) || isProfitTab) {
            rows = rows.filter((rollToRow) => rollToRow.key !== 'rate' && rollToRow.key !== 'balloonAmount');
        }
        if (isProfitTab) {
            rows = rows.filter((rollToRow) => rollToRow.key !== 'downPayment' && rollToRow.key !== 'balloonAmount');
        }

        return rows;
    }, [
        currentTerm,
        rollToTerm,
        adjustTerm,
        currentRate,
        rollToRate,
        adjustRate,
        currentDownPayment,
        rollToDownPayment,
        adjustDownPayment,
        currentSellingPrice,
        rollToSellingPrice,
        adjustSellingPrice,
        rollTo,
        currentTrades,
        rollToTrades,
        adjustTrades,
        currentTaxesAndFeesAsStr,
        adjustTaxesAndFeesAsStr,
        addOnsAsStr,
        adjustAddOnsAsStr,
        activeTab,
        currentActionValue,
        rollToActionValue,
        adjustActionValue,
        isTradeInCompleted,
        isPaymentTab,
        isBalanceDueTab,
        isCashDueTab,
        isLease,
        cashDownLabel,
        rateType
    ]);

    const isCurrentEqualToAdjust = useMemo(() => isEqual(current, adjust), [adjust, current]);

    const handleUpdateRollToAdjust = debounce(
        (newValues: Record<string, number | undefined | RollToOption[]>) => {
            dispatch(
                rollToActionCreators.updateRollToAdjust(newValues, {
                    actionValueType: activeTab
                })
            );
            dispatch(rollToActionCreators.setUnsavedChanges(true));
        },
        defaultDebounceDuration,
        { trailing: true }
    );

    const handleChangeAdjustedValue = (index: number, event: InterstateOnChangeEvent<TextInputEventValue>) => {
        const newValue = event.target.value.toString();

        const isInvalidValue = typeof newValue === 'undefined' || newValue === '' || isNaN(parseFloat(newValue));

        if (isInvalidValue) {
            handleUpdateRollToAdjust.cancel();
            return;
        }

        const row = rollToRows[index];
        const parsedValue = parseFloat(newValue);

        // Handle multiple trade-ins - build trade array
        if (row.isTradeInRow && row.key.startsWith('trade_')) {
            const tradeIndex = (row.callerItemId || 1) - 1;
            const updatedTrades = [...adjustTrades];
            const currentTradeValue = currentTrades[tradeIndex]?.value;
            const isAdjusted = currentTradeValue !== parsedValue;

            // Update the specific trade item
            updatedTrades[tradeIndex] = { value: parsedValue, adjusted: isAdjusted, callerItemId: row.callerItemId };
            const newRollToValues = { ...values.current, tradeIns: updatedTrades };

            values.current = newRollToValues;
            handleUpdateRollToAdjust(newRollToValues);
        } else {
            // Handle other fields (term, rate, downPayment, sellingPrice, etc.)
            const newRollToValues = { ...values.current, [row.key]: parsedValue };

            values.current = newRollToValues as Record<number, number | undefined>;
            handleUpdateRollToAdjust(newRollToValues);
        }
    };

    const handleRollToClick = (row: Row) => {
        let rawValue = row.rollToRaw;

        if (rawValue === undefined || isNaN(rawValue)) return;

        if (rawValue < 0) {
            rawValue = 0;
        }
        // Handle multiple trade-ins
        if (row.isTradeInRow && row.key.startsWith('trade_')) {
            const tradeIndex = (row.callerItemId || 1) - 1;
            const updatedTrades = [...adjustTrades];
            updatedTrades[tradeIndex] = { value: rawValue, adjusted: true, callerItemId: row.callerItemId };

            const newRollToValues = { ...values.current, tradeIns: updatedTrades };
            values.current = newRollToValues;

            // Immediately update the adjust field in the store, then trigger payment call
            dispatch(rollToActionCreators.updateAdjustRollToChanges({ ...adjust, tradeIns: updatedTrades }));
            handleUpdateRollToAdjust(newRollToValues);
        } else {
            const newRollToValues = { ...values.current, [row.key]: rawValue };
            values.current = newRollToValues as Record<number, number | undefined>;

            // Immediately update the adjust field in the store
            dispatch(rollToActionCreators.updateAdjustRollToChanges({ ...adjust, [row.key]: rawValue }));
            handleUpdateRollToAdjust(newRollToValues);
        }
    };

    const handleRevertToOriginal = () => {
        dispatch(
            rollToActionCreators.revertRollToOriginal({
                actionValueType: activeTab
            })
        );
        dispatch(rollToActionCreators.setUnsavedChanges(false));
        values.current = null;
    };

    if (!hasRollToOptions) return null;

    return (
        <>
            <RollToPaymentHeader
                gridCategory={`Roll-To ${activeTab}`}
                gridAction={handleRevertToOriginal}
                actionMessage={'Revert To Original'}
                disabled={isCurrentEqualToAdjust}
            />
            <RollToPaymentGrid
                headers={headers}
                rows={rollToRows}
                getAdjustedFieldInputValue={handleChangeAdjustedValue}
                handleRollToClick={handleRollToClick}
            />
        </>
    );
};

export default RollToPaymentOptions;
